import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import {
  IApiResult,
  IModal,
  IOptionItem,
  ITableColumn,
} from '../../interfaces/app.interface';
import { removeComponentModalStore } from '../../stores/componentModal.store';
import { modalStore, removeModalStore } from '../../stores/modal.store';
import PbSection from '../PbSection/PbSection.component';
import { PbFormGrid } from '../PbFormGrid';
import { PbFormGridCol } from '../PbFormGridCol';
import { PbAgGridReact } from '../PbAgGridReact';
import { Button, Popover, Select, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import * as cmnApi from '../../apis/cmn.api';
import * as slsApi from '../../apis/sls.api';
import { phoneNumberformatter } from '../../utils/app.util';

interface IComponentProps {
  id: string;
  callback?: () => void;
  onClose?: () => void;
}

const isSuccessResponse = (code: string = '') =>
  ['0000', '200'].includes(String(code));

/**
 * 영업 관리 > 영업 기회 등록 > 기본 조회 명단 관리
 * @param id 컴포넌트 모달 아이디
 * @param callback 저장 후 콜백
 * @constructor
 */
const MN2302220202_BasicReadAuthManager = ({
  id,
  callback,
  onClose,
}: PropsWithChildren<IComponentProps>) => {
  const [, setRemoveComponentModal] =
    useRecoilState<string | null>(removeComponentModalStore);

  const [, setModal] = useRecoilState<IModal>(modalStore);
  const [, setRemoveModal] = useRecoilState<any>(removeModalStore);

  const basicReadAuthTableRef = useRef<any>(null);
  const targetUserTableRef = useRef<any>(null);
  const originalDataRef = useRef<any[]>([]);

  const [basicReadAuthTable, setBasicReadAuthTable] = useState<{
    column: any;
    data: any;
    loading?: boolean;
  }>({
    column: [],
    data: [],
    loading: false,
  });

  const [targetUserSearchSelect, setTargetUserSearchSelect] =
    useState<string>('name');
  const [targetUserSearchSelectItem, setTargetUserSearchSelectItem] = useState<
    IOptionItem[]
  >([]);
  const [targetUserSearchInput, setTargetUserSearchInput] = useState<string>('');
  const [targetUserTable, setTargetUserTable] = useState<{
    column: any;
    data: any;
    loading?: boolean;
  }>({
    column: [],
    data: [],
    loading: false,
  });
  const [visibleTargetUser, setVisibleTargetUser] = useState<boolean>(false);

  const runCallback = () => {
    if (callback !== undefined) {
      callback();
    }
  };

  const getBasicReadAuthTableData = async () => {
    setBasicReadAuthTable((pre: { column: any; data: any }) => ({
      ...pre,
      loading: true,
    }));

    try {
      const data: IApiResult = await slsApi.getSlsBscXpsr({});

      const list = data.data.list || [];
      originalDataRef.current = list;

      setBasicReadAuthTable((pre: { column: ITableColumn[]; data: any }) => ({
        ...pre,
        data: list,
        loading: false,
      }));
    } catch (error: any) {
      setBasicReadAuthTable((pre: { column: ITableColumn[]; data: any }) => ({
        ...pre,
        loading: false,
      }));

      notifications.show({
        title: '오류',
        message: (
          <span className="text-lg">
            기본 조회 명단을 불러오지 못했습니다.
          </span>
        ),
        color: 'pink',
      });
    }
  };

  const handleMandatoryButton_onClick = (row: any) => {
    const nextManditoryYn: string = row.manditoryYn === 'Y' ? 'N' : 'Y';

    setBasicReadAuthTable((pre: { column: ITableColumn[]; data: any[] }) => ({
      ...pre,
      data: pre.data.map((item: any) =>
        item.userId === row.userId
          ? { ...item, manditoryYn: nextManditoryYn }
          : item,
      ),
    }));
  };

  const handleSelectedDeleteButton_onClick = () => {
    const selectedRows: any[] =
      basicReadAuthTableRef.current?.api?.getSelectedRows() || [];

    if (selectedRows.length === 0) {
      notifications.show({
        title: '확인',
        message: (
          <span className="text-lg">삭제할 사용자를 체크하여 선택하세요.</span>
        ),
        color: 'pink',
      });

      return;
    }

    setModal({
      title: '확인',
      content: '선택하신 기본 조회 명단을 삭제하시겠습니까?',
      useOkayButton: false,
      useCancelButton: true,
      cancelButtonLabel: '아니오',
      button: (
        <Button
          color="indigo"
          radius="xl"
          size="md"
          onClick={() => {
            setRemoveModal(true);

            const selectedUserIds = selectedRows.map((r: any) => r.userId);

            setBasicReadAuthTable((pre: { column: any; data: any[] }) => ({
              ...pre,
              data: pre.data.filter(
                (item: any) => !selectedUserIds.includes(item.userId),
              ),
            }));

            notifications.show({
              title: '확인',
              message: (
                <span className="text-lg">
                  선택한 항목을 삭제하였습니다. 저장 버튼을 눌러 반영하세요.
                </span>
              ),
              color: 'indigo',
            });
          }}
        >
          예
        </Button>
      ),
    });
  };

  const getTargetUserTableData = async () => {
    setTargetUserTable((pre: { column: any; data: any }) => ({
      ...pre,
      loading: true,
    }));

    try {
      const data: IApiResult = await cmnApi.getCmnUsers({
        searchType: targetUserSearchSelect,
        searchKeyword: targetUserSearchInput,
        pageSize: 10000,
      });

      const currentUserIdList: string[] = (basicReadAuthTable.data || []).map(
        (item: any) => item.userId,
      );

      setTargetUserTable((pre: { column: ITableColumn[]; data: any }) => ({
        ...pre,
        data: (data.data.list || []).filter(
          (item: any) => !currentUserIdList.includes(item.userId),
        ),
        loading: false,
      }));
    } catch (error: any) {
      setTargetUserTable((pre: { column: ITableColumn[]; data: any }) => ({
        ...pre,
        loading: false,
      }));
    }
  };

  const handleTargetUserApplyButton_onClick = () => {
    const selectedRows: any[] =
      targetUserTableRef.current?.api?.getSelectedRows() || [];

    if (selectedRows.length === 0) {
      notifications.show({
        title: '확인',
        message: (
          <span className="text-lg">추가할 대상자를 체크하여 선택하세요.</span>
        ),
        color: 'pink',
      });

      return;
    }

    const currentUserIds: string[] = (basicReadAuthTable.data || []).map(
      (item: any) => item.userId,
    );
    const newRows = selectedRows
      .filter((item: any) => !currentUserIds.includes(item.userId))
      .map((item: any) => ({ ...item, manditoryYn: 'N' }));

    setVisibleTargetUser(false);
    setTargetUserSearchInput('');

    setBasicReadAuthTable((pre: { column: any; data: any[] }) => ({
      ...pre,
      data: [...pre.data, ...newRows],
    }));

    notifications.show({
      title: '확인',
      message: (
        <span className="text-lg">
          대상자를 추가하였습니다. 저장 버튼을 눌러 반영하세요.
        </span>
      ),
      color: 'indigo',
    });
  };

  // 기본 조회 명단 관리 > 저장 버튼을 클릭함
  const handleSaveButton_onClick = () => {
    setModal({
      title: '확인',
      content: '저장하시겠습니까?',
      useOkayButton: false,
      useCancelButton: true,
      cancelButtonLabel: '아니오',
      button: (
        <Button
          color="indigo"
          radius="xl"
          size="md"
          onClick={async () => {
            setRemoveModal(true);

            try {
              const original: any[] = originalDataRef.current;
              const current: any[] = basicReadAuthTable.data;

              const originalIds = original.map((item: any) => item.userId);
              const currentIds = current.map((item: any) => item.userId);

              // 삭제 대상: 원본에 있고 현재에 없는 항목
              const toDelete = original.filter(
                (item: any) => !currentIds.includes(item.userId),
              );

              // 추가 대상: 현재에 있고 원본에 없는 항목
              const toAdd = current.filter(
                (item: any) => !originalIds.includes(item.userId),
              );

              // 수정 대상: 양쪽에 있고 manditoryYn이 다른 항목
              const toUpdate = current.filter((item: any) => {
                const orig = original.find((o: any) => o.userId === item.userId);
                return orig && orig.manditoryYn !== item.manditoryYn;
              });

              await Promise.all([
                ...toDelete.map((item: any) =>
                  slsApi.deleteSlsBscXpsr({ userId: item.userId }),
                ),
                ...toAdd.map((item: any) =>
                  slsApi.postSlsBscXpsr({
                    userId: item.userId,
                    manditoryYn: item.manditoryYn,
                  }),
                ),
                ...toUpdate.map((item: any) =>
                  slsApi.putSlsBscXpsr({
                    userId: item.userId,
                    manditoryYn: item.manditoryYn,
                  }),
                ),
              ]);

              // 저장 완료 후 원본 기준 업데이트
              originalDataRef.current = [...current];

              notifications.show({
                title: '확인',
                message: <span className="text-lg">저장하였습니다.</span>,
                color: 'indigo',
              });

              runCallback();
            } catch (error: any) {
              notifications.show({
                title: '오류',
                message: (
                  <span className="text-lg">
                    {error?.message || '저장에 실패하였습니다.'}
                  </span>
                ),
                color: 'pink',
              });
            }
          }}
        >
          예
        </Button>
      ),
    });
  };

  const initTargetUserSearchSelect = () => {
    setTargetUserSearchSelectItem([
      {
        label: '이름',
        value: 'name',
      },
      {
        label: '닉네임',
        value: 'nick_name',
      },
    ]);
  };

  const initBasicReadAuthTable = () => {
    setBasicReadAuthTable((pre: { column: any; data: any }) => ({
      ...pre,
      column: [
        {
          field: 'checkbox',
          width: 60,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          suppressSizeToFit: true,
        },
        {
          headerName: '회사',
          field: 'cmpnName',
          width: 140,
          suppressSizeToFit: true,
        },
        {
          headerName: '부서',
          field: 'dprtName',
          width: 130,
          suppressSizeToFit: true,
        },
        {
          headerName: '직급',
          field: 'pstnName',
          width: 110,
          suppressSizeToFit: true,
        },
        {
          headerName: '이름',
          field: 'name',
          width: 100,
          suppressSizeToFit: true,
        },
        {
          headerName: '로그인 ID',
          field: 'lgnId',
          width: 120,
          suppressSizeToFit: true,
        },
        {
          headerName: '이메일',
          field: 'ml',
          width: 220,
          suppressSizeToFit: true,
        },
        {
          headerName: '필수 여부',
          field: 'manditoryYn',
          width: 130,
          suppressSizeToFit: true,
          sortable: false,
          cellRenderer: (params: any) => (
            <Button
              color={params.data.manditoryYn === 'Y' ? 'pink' : 'gray'}
              variant={params.data.manditoryYn === 'Y' ? 'filled' : 'outline'}
              radius="xl"
              size="xs"
              onClick={() => handleMandatoryButton_onClick(params.data)}
            >
              {params.data.manditoryYn === 'Y' ? '필수' : '선택'}
            </Button>
          ),
        },
      ],
    }));
  };

  const initTargetUserTable = () => {
    setTargetUserTable((pre: { column: any; data: any }) => ({
      ...pre,
      column: [
        {
          field: 'checkbox',
          width: 60,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          suppressSizeToFit: true,
        },
        {
          headerName: '회사',
          field: 'cmpnName',
          width: 130,
          suppressSizeToFit: true,
        },
        {
          headerName: '본부',
          field: 'hdqrName',
          width: 130,
          suppressSizeToFit: true,
        },
        {
          headerName: '부서',
          field: 'dprtName',
          width: 120,
          suppressSizeToFit: true,
        },
        {
          headerName: '직급',
          field: 'pstnName',
          width: 100,
          suppressSizeToFit: true,
        },
        {
          headerName: '이름',
          field: 'name',
          width: 100,
          suppressSizeToFit: true,
        },
        {
          headerName: '휴대폰번호',
          field: 'celPhn',
          width: 150,
          suppressSizeToFit: true,
          valueFormatter: (params: any) => phoneNumberformatter(params.value),
        },
        {
          headerName: '이메일',
          field: 'ml',
          width: 220,
          suppressSizeToFit: true,
        },
      ],
    }));
  };

  useEffect(() => {
    initTargetUserSearchSelect();
    initBasicReadAuthTable();
    initTargetUserTable();

    return () => {};
  }, []);

  useEffect(() => {
    if (basicReadAuthTable.column.length === 0) {
      return;
    }

    getBasicReadAuthTableData();

    return () => {};
  }, [basicReadAuthTable.column.length]);

  useEffect(() => {
    if (targetUserTable.column.length === 0) {
      return;
    }

    getTargetUserTableData();

    return () => {};
  }, [targetUserTable.column.length, basicReadAuthTable.data]);

  return (
    <div className="space-y-7">
      <PbSection
        label="기본 조회 명단"
        rightForm={
          <div className="flex justify-end items-center space-x-2">
            <Button
              color="pink"
              radius="xl"
              onClick={handleSelectedDeleteButton_onClick}
            >
              선택 삭제
            </Button>

            <Popover
              opened={visibleTargetUser}
              onClose={() => setVisibleTargetUser(false)}
              position="top-end"
              transitionProps={{ transition: 'pop' }}
              withArrow={false}
              shadow="xl"
              zIndex={100}
              offset={10}
            >
              <Popover.Target>
                <Button
                  onClick={() => setVisibleTargetUser(true)}
                  color="indigo"
                  radius="xl"
                >
                  추가
                </Button>
              </Popover.Target>

              <Popover.Dropdown className="border-1 !border-gray-500">
                <PbSection label="추가할 대상자 선택">
                  <div className="space-y-3">
                    <PbFormGrid cols={1}>
                      <PbFormGridCol label="검색">
                        <Select
                          onChange={(event: any) =>
                            setTargetUserSearchSelect(event || 'name')
                          }
                          data={targetUserSearchSelectItem}
                          value={targetUserSearchSelect}
                        />

                        <TextInput
                          placeholder="검색어를 입력하세요."
                          onChange={(event: any) =>
                            setTargetUserSearchInput(event.currentTarget.value)
                          }
                          onKeyUp={(event: any) => {
                            if (event.keyCode === 13) {
                              getTargetUserTableData();
                            }
                          }}
                          value={targetUserSearchInput}
                          className="w-1/5"
                        />

                        <div className="flex justify-center items-center space-x-2">
                          <Button
                            color="indigo"
                            radius="xl"
                            onClick={getTargetUserTableData}
                          >
                            검색
                          </Button>
                        </div>
                      </PbFormGridCol>
                    </PbFormGrid>

                    <div style={{ width: '1100px' }} className="h-96">
                      <PbAgGridReact
                        refs={targetUserTableRef}
                        columnDefs={targetUserTable.column}
                        rowData={targetUserTable.data}
                        loading={targetUserTable.loading}
                        defaultColDef={{
                          resizable: true,
                          sortable: true,
                          wrapHeaderText: false,
                          autoHeaderHeight: true,
                        }}
                        rowSelection="multiple"
                        sizeColumnsToFit={true}
                      />
                    </div>

                    <div className="flex justify-end items-center space-x-2">
                      <Button
                        color="indigo"
                        radius="xl"
                        onClick={handleTargetUserApplyButton_onClick}
                      >
                        적용
                      </Button>
                    </div>
                  </div>
                </PbSection>
              </Popover.Dropdown>
            </Popover>

            <Button
              color="indigo"
              radius="xl"
              onClick={handleSaveButton_onClick}
            >
              저장
            </Button>
          </div>
        }
      >
        <div className="w-full h-96">
          <PbAgGridReact
            refs={basicReadAuthTableRef}
            columnDefs={basicReadAuthTable.column}
            rowData={basicReadAuthTable.data}
            loading={basicReadAuthTable.loading}
            defaultColDef={{
              resizable: true,
              sortable: true,
              wrapHeaderText: false,
              autoHeaderHeight: true,
            }}
            rowSelection="multiple"
            sizeColumnsToFit={true}
          />
        </div>
      </PbSection>

      <div className="component-modal-button-area">
        <div>
          <Button
            variant="outline"
            color="gray"
            radius="xl"
            onClick={() => {
              if (onClose) {
                onClose();
                return;
              }

              setRemoveComponentModal(id);
            }}
          >
            닫기
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MN2302220202_BasicReadAuthManager;
